import ActionButton from '@deltek/specification-client/core/Buttons/ActionButton'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    MoveItemToAnotherArrayProps,
    moveItemToAnotherArray,
} from '@deltek/specification-client/core/Utilities/formUtils'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Switch from '@material-ui/core/Switch'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import SettingsIcon from '@material-ui/icons/Settings'
import LoadingBackdrop from 'bpm/components/loadingBackdrop/LoadingBackdrop'
import ConfirmationDialog from 'bpm/pages/CompanyAccountsSettingsPage/components/ConfirmationDialog'
import {
    defaultSortOrder,
    defaultTableSettings,
} from 'bpm/pages/ProductCardManagementPage/ProductListingsPage/ProductListingsPage.constants'
import { prepareTableSettingsFromData } from 'bpm/pages/ProductCardManagementPage/ProductListingsPage/useListingQueryHelper'

import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import { useListingPageStore } from 'bpm/pages/ProductCardManagementPage/store'
import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'
import {
    isDeltekUserorAdmin,
    isUserBPMAdmin,
} from 'bpm/pages/ProductsPage/productsUtils'
import clsx from 'clsx'
import React, {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { ColumnInstance, IdType } from 'react-table'
import CustomHoverTooltip from '../CustomHoverTooltip'
import { sharedTableStyles } from '../Spectable.styles'
import { DroppableContainer } from './DroppableContainer'
import TableSettingsButton from './TableSettingsButton'
import { TableSettingsState } from './store'
import useTableSettingsStoreHelper from './useTableSettingsStoreHelper'

const { errorColor, defaultSecondary } = listingColors

export const tableSettingsStyles = makeStyles((theme: Theme) => ({
    addButtonSizeSmall: {
        padding: theme.spacing(1),
    },
    actionContainer: {
        padding: 12,
        display: 'flex',
        justifyContent: 'space-between',
    },
    actionButtonWrapper: {
        display: 'flex',
    },
    columnContainer: {
        marginTop: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        border: '1px solid #c4c4c4',
    },
    columnContentBorder: {
        borderBottom: '1px solid #c4c4c4',
    },
    columnContentTitleMargin: {
        marginRight: theme.spacing(1),
    },
    columnContentList: {
        maxHeight: 430,
        overflowY: 'auto',
        minHeight: pxToRem(40),
    },
    columnHeader: {
        backgroundColor: '#0069AA',
        color: '#fff',
        paddingLeft: 12,
    },
    dialogClose: {
        position: 'absolute',
        top: 12,
        right: 12,
    },
    dialogContent: {
        height: 600,
    },
    groupOption: {
        marginBottom: 10,
    },
    tableSettingsDivider: {
        borderTop: '1px solid #0069AA',
        borderBottom: '1px solid #0069AA',
    },
    tableSettingsTitle: {
        color: '#0069AA',
    },
    tableSettingsDialogSm: {
        maxWidth: 800,
    },
    groupEnabledSwitchPrimary: {
        '&.Mui-checked': {
            color: '#13b5ea',
            '& + .MuiSwitch-track': {
                backgroundColor: '#13b5ea',
            },
        },
    },
    rowHeight: {
        height: theme.spacing(5),
    },
    rowPadding: {
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(0.5),
    },
    rowTitle: {
        color: '#13B5EA',
        fontSize: pxToRem(13),
    },
}))

export interface IdTitleProps {
    id: string
    title: string
}

const compareIdTitleProps = (a: IdTitleProps, b: IdTitleProps) => {
    if (a.title < b.title) return -1
    if (a.title > b.title) return 1
    return 0
}

export interface PinningProps {
    /**
     * Sets if the first column is going to be pinned / sticky.
     * Action columns will always going to be pinned and will not be included in this property
     */
    firstColumnPinned: boolean
    /**
     * Sets if the last column is going to be pinned / sticky.
     * Action columns will always going to be pinned and will not be included in this property
     */
    lastColumnPinned: boolean
}

export interface TableSettingsProps<TData extends Object> {
    initialPinningProps?: PinningProps
    /**
     * Sets the allowable maximum grouping.
     * Default 0, this will have no restriction.
     */
    maxGroupCount?: number
    /**
     * The event that gets called when table settings are saved.
     */
    onTableSettingsChange?: (response: PinningProps) => void
    /**
     * Shows / hides group option setting. Default: true
     */
    showGroupOptionSetting?: boolean
    /**
     * Shows / hides the pinning column. Default: true
     */
    showPinning?: boolean
    /**
     * Column instance coming from react-table.
     */
    columns?: ColumnInstance<TData>[]
    /**
     * Sets the default for the grouping option toggle. Default: true
     */
    allowGrouping?: boolean
    /**
     * Shows or hides the dialog on load. Default false
     */
    initialDialogOpen?: boolean
    /**
     * Initial stsate of the group by columns
     */
    initialGroupByColumnIds?: IdType<TData>[]
    /**
     * Initial state of the hidden columns (available columns)
     */
    initialHiddenColumnIds?: IdType<TData>[]
    /**
     * Saves the table view settings
     */
    saveTableViewSettings?: (state: TableSettingsState) => void
    /**
     * Sets the value of the hidden columns for react-table to consume.
     */
    setHiddenColumns?: (hiddenColumns: IdType<TData>[]) => void
    /**
     * Sets the column order for react-table to consume.
     */
    setColumnOrder?: (columnOrder: IdType<TData>[]) => void
    /**
     * Selected columns
     */
    visibleColumns?: ColumnInstance<TData>[]
    /**
     * default value=true, on setting false, if grouping is being shown, they will not be collapsible.
     */
    canToggleGroup?: boolean

    setTableGrouping?: (ids: IdTitleProps[]) => void
    /**
     * shows the collapse checkbox
     */
    showCollapseToggle?: boolean
    /**
     * Sets the collapsed group columns
     */
    setCollapsedGroupColumns?: (
        collapsedColumns: Record<string, boolean> | undefined
    ) => void

    collapsedGroupColumns?: Record<string, boolean> | undefined
}

const selectedColumnsDroppableId = 'selectedColumnsDroppableId'
const groupsDroppableId = 'groupsDroppableId'

export const TableSettings = <TData extends Object>({
    columns,
    initialDialogOpen = false,
    initialGroupByColumnIds,
    initialHiddenColumnIds,
    showGroupOptionSetting = true,
    showCollapseToggle,
    showPinning = true,
    maxGroupCount = 0,
    visibleColumns,
    saveTableViewSettings,
    setHiddenColumns,
    setColumnOrder,
    setCollapsedGroupColumns,
}: TableSettingsProps<TData>) => {
    const {
        groupEnabledState,
        availableColumnsState,
        groupColumnsState,
        selectedColumnsState,
        pinningPropsState,
        collapseColumnCheckedState,
        setTableSettings,
        setPinningProps,
    } = useTableSettingsStoreHelper()

    const currentUserInfoResponse = useContext(AuthContext)

    const { isAdminAccess, isBPMReviewer } = useMemo(() => {
        const {
            isDeltekAdmin,
            isDeltekPublishingAuthor,
            isDeltekAuthor,
            isDeltekReviewer,
        } = isDeltekUserorAdmin(currentUserInfoResponse)

        const { isBPMReviewer } = isUserBPMAdmin(currentUserInfoResponse)

        return {
            isBPMReviewer,
            isAdminAccess:
                isDeltekAdmin ||
                isDeltekPublishingAuthor ||
                isDeltekAuthor ||
                isDeltekReviewer,
        }
    }, [currentUserInfoResponse])

    const isListingLoading = useListingPageStore((state) => state.isLoading)
    const setIsListingLoading = useListingPageStore(
        (state) => state.setIsListingLoading
    )

    const [showDialogWarning, setShowDialogWarning] = useState(false)
    const [groupEnabled, setLocalGroupEnabled] = useState(groupEnabledState)
    const [availableColumns, setLocalAvailableColumns] = useState(
        availableColumnsState
    )
    const [groupColumns, setLocalGroupColumns] = useState(groupColumnsState)
    const [selectedColumns, setLocalSelectedColumns] = useState(
        selectedColumnsState
    )
    const [pinningProps, setLocalPinningProps] = useState(pinningPropsState)
    const [collapseColumnChecked, setLocalCollapseColumnChecked] = useState(
        collapseColumnCheckedState
    )

    const tableSettingsClasses = tableSettingsStyles()
    const sharedTableClasses = sharedTableStyles()
    const [dialogOpen, setDialogOpen] = useState(initialDialogOpen)
    const [filteredAvailableColumns, setFilteredAvailableColumns] = useState<
        IdTitleProps[]
    >([])
    const [searchValue, setSearchValue] = useState('')
    const [shouldResetSortOrder, setShouldResetSortOrder] = useState(false)
    const [shouldProcessSettings, setShouldProcessSettings] = useState(false)

    // Check if current settings match default settings
    const isCurrentSettingsMatchingDefaults = useMemo(() => {
        const {
            availableColumns: defaultAvailableColumns,
            groupEnabledSettings: defaultGroupEnabled,
            selectedColumnsSettings: defaultSelectedColumns,
            groupColumnsSettings: defaultGroupColumns,
            pinningPropsSettings: defaultPinningProps,
            collapseColumnCheckedSettings: defaultCollapsedColumns,
        } = prepareTableSettingsFromData(
            defaultTableSettings,
            isAdminAccess,
            isBPMReviewer
        )

        return (
            isEqual(groupEnabled, defaultGroupEnabled) &&
            isEqual(availableColumns, defaultAvailableColumns) &&
            isEqual(groupColumns, defaultGroupColumns) &&
            isEqual(selectedColumns, defaultSelectedColumns) &&
            isEqual(pinningProps, defaultPinningProps) &&
            isEqual(collapseColumnChecked, defaultCollapsedColumns)
        )
    }, [
        groupEnabled,
        availableColumns,
        groupColumns,
        selectedColumns,
        pinningProps,
        collapseColumnChecked,
        isAdminAccess,
        isBPMReviewer,
    ])

    const handleAcknowledgeDialogWarning = useCallback(() => {
        setShowDialogWarning(false)
    }, [])

    const handleAddAllClick = (
        _e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        if (availableColumns.length === 0) return
        const selectedColumnsClone = Array.from(
            JSON.parse(JSON.stringify(selectedColumns))
        ) as IdTitleProps[]

        if (!searchValue) {
            setLocalSelectedColumns([
                ...selectedColumnsClone,
                ...availableColumns,
            ])
            setLocalAvailableColumns([])
            return
        }

        const filteredAvailableColumnsClone = Array.from(
            JSON.parse(JSON.stringify(filteredAvailableColumns))
        ) as IdTitleProps[]
        const filteredAvailableColumnsCloneIds = filteredAvailableColumnsClone.map(
            (col) => col.id
        )
        setLocalSelectedColumns([
            ...selectedColumnsClone,
            ...filteredAvailableColumnsClone,
        ])
        setLocalAvailableColumns(
            availableColumns.filter(
                (col: IdTitleProps) =>
                    !filteredAvailableColumnsCloneIds.includes(col.id)
            )
        )
    }

    const handleAvailableColumnAddClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const colId = event.currentTarget.dataset['columnId']
        const fromIndex = availableColumns.findIndex(
            (col: IdTitleProps) => col.id === colId
        )
        const toIndex = selectedColumns.length

        const moveResponse = moveItemToAnotherArray<IdTitleProps>({
            fromArray: availableColumns,
            toArray: selectedColumns,
            fromIndex,
            toIndex,
        })

        setLocalAvailableColumns(moveResponse.fromArray)
        setLocalSelectedColumns(moveResponse.toArray)
    }

    const handleDialogOpen = useCallback(() => {
        setLocalGroupEnabled(groupEnabledState)
        setLocalAvailableColumns(availableColumnsState)
        setLocalGroupColumns(groupColumnsState)
        setLocalSelectedColumns(selectedColumnsState)
        setLocalPinningProps(pinningPropsState)
        setLocalCollapseColumnChecked(collapseColumnCheckedState)

        setDialogOpen(true)
    }, [
        groupEnabledState,
        availableColumnsState,
        groupColumnsState,
        selectedColumnsState,
        pinningPropsState,
        collapseColumnCheckedState,
    ])

    const handleDragDropContextEnd = ({
        active: source,
        over: destination,
    }: DragEndEvent) => {
        if (!destination) return

        const isSourceSelectedColumn =
            source?.data?.current?.sortable?.containerId ===
            selectedColumnsDroppableId

        // same container, reorder only
        if (
            source?.data?.current?.sortable?.containerId ===
            destination?.data?.current?.sortable?.containerId
        ) {
            if (
                source?.data?.current?.sortable?.index ===
                destination?.data?.current?.sortable?.index
            )
                return

            const reorderedColumnInstances = arrayMove(
                isSourceSelectedColumn ? selectedColumns : groupColumns,
                source?.data?.current?.sortable?.index,
                destination?.data?.current?.sortable?.index
            )
            if (isSourceSelectedColumn) {
                setLocalSelectedColumns(reorderedColumnInstances)
            } else {
                setLocalGroupColumns(reorderedColumnInstances ?? [])
            }
            return
        }

        // Disable dropping an item on Group Columns when maxGroup has been reached
        if (
            maxGroupCount !== 0 &&
            groupColumns.length === maxGroupCount &&
            destination?.data?.current?.sortable?.containerId ===
            groupsDroppableId
        ) {
            return
        }

        // Dropping item on a different container
        // will update both containers
        const columnInstanceProps: MoveItemToAnotherArrayProps<IdTitleProps> = {
            fromArray: selectedColumns,
            toArray: groupColumns,
            fromIndex: source?.data?.current?.sortable?.index,
            toIndex: destination?.data?.current?.sortable?.index ?? 0,
        }

        if (!isSourceSelectedColumn) {
            columnInstanceProps.fromArray = groupColumns
            columnInstanceProps.toArray = selectedColumns
        }
        const moveResponse = moveItemToAnotherArray<IdTitleProps>(
            columnInstanceProps
        )

        setLocalSelectedColumns(
            isSourceSelectedColumn
                ? moveResponse.fromArray
                : moveResponse.toArray
        )
        setLocalGroupColumns(
            isSourceSelectedColumn
                ? moveResponse.toArray
                : moveResponse.fromArray
        )
    }

    const handleGroupEnabledChange = (
        _e: ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        // Move any Group Column Items to Selected Columns
        // when user hides the Group Option
        if (!checked) {
            const selectedColumnsClone = Array.from(
                JSON.parse(JSON.stringify(selectedColumns))
            ) as IdTitleProps[]

            setLocalSelectedColumns([...groupColumns, ...selectedColumnsClone])
            setLocalPinningProps({
                firstColumnPinned: false,
                lastColumnPinned: false,
            })
            setLocalGroupColumns([])
            setLocalGroupEnabled(false)
            return
        }

        setLocalGroupEnabled(checked)
    }

    const handlePinClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const droppableId = event.currentTarget.dataset['droppableId']
        const itemIdx = event.currentTarget.dataset['itemIndex']

        // if the droppableId is for the Selected Columns and there are some items in groupColumns,
        // it means the pin will always be the last pin.
        if (
            droppableId === selectedColumnsDroppableId &&
            groupColumns.length > 0
        ) {
            setLocalPinningProps({
                ...pinningProps,
                lastColumnPinned: !pinningProps.lastColumnPinned,
            })
            return
        }

        // else follow the index of the pin
        if (itemIdx === '0') {
            setLocalPinningProps({
                ...pinningProps,
                firstColumnPinned: !pinningProps.firstColumnPinned,
            })
            return
        }
        setLocalPinningProps({
            ...pinningProps,
            lastColumnPinned: !pinningProps.lastColumnPinned,
        })
        return
    }

    const handleRemoveAllClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const droppableId = event.currentTarget.dataset['droppableId']

        const availableColumnsClone = Array.from(
            JSON.parse(JSON.stringify(availableColumns))
        ) as IdTitleProps[]

        // SELECTED COLUMNS Remove All
        if (droppableId === selectedColumnsDroppableId) {
            if (selectedColumns.length === 0) return

            setLocalAvailableColumns(
                [...availableColumnsClone, ...selectedColumns].sort(
                    compareIdTitleProps
                )
            )
            setLocalSelectedColumns([])

            return
        }

        // GROUP COLUMNS Remove All
        if (groupColumns.length === 0) return

        setLocalAvailableColumns(
            [...availableColumnsClone, ...groupColumns].sort(
                compareIdTitleProps
            )
        )
        setLocalGroupColumns([])

        setLocalPinningProps({
            firstColumnPinned: false,
            lastColumnPinned: false,
        })
    }

    const handleRemoveClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        const droppableId = event.currentTarget.dataset['droppableId']
        const fromIndex = parseInt(
            event.currentTarget.dataset['removeId'] ?? '0'
        )

        const isFromArrSelectedColumns =
            droppableId === selectedColumnsDroppableId

        const fromArray = isFromArrSelectedColumns
            ? selectedColumns
            : groupColumns
        const toIndex = availableColumns.length

        const moveResponse = moveItemToAnotherArray<IdTitleProps>({
            fromArray,
            toArray: availableColumns,
            fromIndex,
            toIndex,
        })

        setLocalAvailableColumns(moveResponse.toArray.sort(compareIdTitleProps))
        if (isFromArrSelectedColumns)
            setLocalSelectedColumns(moveResponse.fromArray)
        else setLocalGroupColumns(moveResponse.fromArray ?? [])
    }

    const handleCollapseColumnCheckChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setLocalCollapseColumnChecked({
                ...collapseColumnChecked,
                [event.target.name]: checked,
            })
        },
        [collapseColumnChecked]
    )

    const processSettingsChange = useCallback(() => {
        const selectedColumnIds = selectedColumns.map(
            (col: IdTitleProps) => col.id
        )

        const hiddenColumnIds = availableColumns.map(
            (col: IdTitleProps) => col.id
        )

        setColumnOrder?.(selectedColumnIds)
        setHiddenColumns?.(hiddenColumnIds)
        if (!!collapseColumnChecked && setCollapsedGroupColumns) {
            const collapsedGroupColumns = groupColumns?.reduce(
                (acc: Record<string, boolean>, current: IdTitleProps) => {
                    const { id } = current
                    return { ...acc, [id]: collapseColumnChecked[id] }
                },
                {}
            )
            setCollapsedGroupColumns?.(collapsedGroupColumns)
        }

        let tempTableSettings: Partial<TableSettingsState> = {
            groupEnabled,
            availableColumns,
            selectedColumns,
            groupColumns,
            pinningProps,
            collapseColumnChecked,
        }

        if (shouldResetSortOrder) {
            tempTableSettings.orderBy = defaultSortOrder
            setShouldResetSortOrder(false)
        }

        setTableSettings(tempTableSettings, saveTableViewSettings)

        setIsListingLoading(false)
    }, [
        groupEnabled,
        selectedColumns,
        availableColumns,
        pinningProps,
        groupColumns,
        collapseColumnChecked,
        shouldResetSortOrder,
        setTableSettings,
        saveTableViewSettings,
    ])

    const handleSaveClick = (
        _event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        if (!setPinningProps) {
            return
        }

        if (!selectedColumns.length) {
            setShowDialogWarning(true)
            return
        }

        if (
            isEqual(groupEnabledState, groupEnabled) &&
            isEqual(availableColumnsState, availableColumns) &&
            isEqual(groupColumnsState, groupColumns) &&
            isEqual(selectedColumnsState, selectedColumns) &&
            isEqual(pinningPropsState, pinningProps) &&
            isEqual(collapseColumnCheckedState, collapseColumnChecked) &&
            !shouldResetSortOrder
        ) {
            setDialogOpen(false)
            return
        }
        setIsListingLoading(true)
        setDialogOpen(false)
        setShouldProcessSettings(true)
    }

    const handleSearchAvailableColumns = (
        event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setSearchValue(event.currentTarget.value)

    const setToInitialValues = useCallback(
        (reset?: boolean) => {
            const availableColumnDefaultState: IdTitleProps[] = initialHiddenColumnIds
                ?.filter((colId) => !initialGroupByColumnIds?.includes(colId))
                .map((colId) => {
                    const currentCol = columns?.find((col) => col.id === colId)
                    return {
                        id: colId,
                        title: currentCol?.Header?.toString() ?? '',
                    }
                }) as IdTitleProps[]

            const groupByColumnDefaultState: IdTitleProps[] = initialGroupByColumnIds?.map(
                (colId) => {
                    const currentCol = columns?.find((col) => col.id === colId)
                    return {
                        id: colId,
                        title: currentCol?.Header?.toString() ?? '',
                    } as IdTitleProps
                }
            ) as IdTitleProps[]
            const selectedColumnDefaultState: IdTitleProps[] = visibleColumns
                ?.filter(
                    (visibleCol) =>
                        columns?.find((col) => col.id === visibleCol.id) &&
                        !initialGroupByColumnIds?.find(
                            (groupBy) => groupBy === visibleCol.id
                        )
                )
                .map((visibleCol) => {
                    return {
                        id: visibleCol.id,
                        title: visibleCol?.Header?.toString() ?? '',
                    } as IdTitleProps
                }) as IdTitleProps[]

            const sortedAvailableColumnDefaultState = availableColumnDefaultState.sort(
                compareIdTitleProps
            )

            setLocalAvailableColumns(sortedAvailableColumnDefaultState)
            setLocalGroupColumns(groupByColumnDefaultState ?? [])
            setLocalSelectedColumns(selectedColumnDefaultState)
        },
        [
            columns,
            initialHiddenColumnIds,
            initialGroupByColumnIds,
            visibleColumns,
        ]
    )

    const handleDialogClose = useCallback((event?: any, reason?: string) => {
        if (reason === 'backdropClick') {
            return
        }
        setDialogOpen(false)
        setToInitialValues()
        setLocalGroupEnabled(groupEnabledState)
    }, [setToInitialValues, groupEnabledState])

    const handleResetTableSettings = useCallback(() => {
        const {
            availableColumns: defaultAvailableColumns,
            groupEnabledSettings: defaultGroupEnabled,
            selectedColumnsSettings: defaultSelectedColumns,
            groupColumnsSettings: defaultGroupColumns,
            pinningPropsSettings: defaultPinningProps,
            collapseColumnCheckedSettings: defaultCollapsedColumns,
        } = prepareTableSettingsFromData(
            defaultTableSettings,
            isAdminAccess,
            isBPMReviewer
        )

        setLocalGroupColumns(defaultGroupColumns)
        setLocalAvailableColumns(defaultAvailableColumns)
        setLocalSelectedColumns(defaultSelectedColumns)
        setLocalPinningProps(defaultPinningProps)
        setLocalCollapseColumnChecked(defaultCollapsedColumns)
        setLocalGroupEnabled(defaultGroupEnabled)
        setShouldResetSortOrder(true)
    }, [isAdminAccess, isBPMReviewer])

    /** Sets the availableColumns, groupByColumns and selectedColumnDefaultState
     * on columns, initialHiddenColumnIds, and initialGroupByColumnIds
     * change from the parent container */
    useEffect(() => {
        if (
            !columns ||
            !initialGroupByColumnIds ||
            !initialHiddenColumnIds ||
            !visibleColumns

        ) {
            return
        }
        setToInitialValues()
    }, [
        columns,
        initialHiddenColumnIds,
        initialGroupByColumnIds,
        visibleColumns,
        setToInitialValues,
    ])

    // filter the available columns based on user's search
    useEffect(() => {
        if (!searchValue) {
            setFilteredAvailableColumns(availableColumns)
            return
        }

        setFilteredAvailableColumns(
            availableColumns.filter((col: IdTitleProps) =>
                col.title.toLowerCase().includes(searchValue.toLowerCase())
            )
        )
    }, [searchValue, availableColumns])

    useEffect(() => {
        if (shouldProcessSettings && !dialogOpen) {
            processSettingsChange()
            setShouldProcessSettings(false)
        }
    }, [shouldProcessSettings, dialogOpen])

    return (
        <>
            <IconButton
                color="default"
                onClick={handleDialogOpen}
                className="settings-icon-button"
                aria-label="Table Settings"
            >
                <SettingsIcon fontSize="small" />
            </IconButton>
            <Dialog
                onClose={handleDialogClose}
                open={dialogOpen}
                fullWidth
                classes={{
                    paperWidthSm: tableSettingsClasses.tableSettingsDialogSm,
                }}
                className="table-settings-dialog"
            >
                <DialogTitle disableTypography>
                    <Typography
                        variant="h6"
                        className={tableSettingsClasses.tableSettingsTitle}
                    >
                        Table Settings
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleDialogClose}
                        className={clsx(
                            tableSettingsClasses.dialogClose,
                            'table-settings-dialog-heading-close-button'
                        )}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    classes={{
                        dividers: tableSettingsClasses.tableSettingsDivider,
                    }}
                    className={clsx(
                        tableSettingsClasses.dialogContent,
                        'table-settings-dialog-content'
                    )}
                >
                    <GridContainer spacing={2}>
                        {/* Left side of Table settings */}
                        <GridItem
                            sm={12}
                            md={6}
                            className={sharedTableClasses.fullWidth}
                        >
                            <TextField
                                variant="outlined"
                                size="small"
                                fullWidth
                                label="Search Available Columns"
                                onChange={handleSearchAvailableColumns}
                                value={searchValue}
                                inputProps={{
                                    className:
                                        'search-available-columns-textfield',
                                    'aria-autocomplete': 'none',
                                    autoComplete: 'chrome-off',
                                    'aria-label': 'Search Available Columns',
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <div
                                className={clsx(
                                    sharedTableClasses.fullWidth,
                                    tableSettingsClasses.columnContainer,
                                    'column-container'
                                )}
                            >
                                <div
                                    className={clsx(
                                        sharedTableClasses.fullWidth,
                                        tableSettingsClasses.columnHeader,
                                        'column-header'
                                    )}
                                >
                                    <GridContainer
                                        alignItems="center"
                                        className={clsx(
                                            tableSettingsClasses.rowHeight,
                                            'available-columns-title-row'
                                        )}
                                    >
                                        <GridItem
                                            xs={9}
                                            className="available-columns-title-container"
                                        >
                                            {`AVAILABLE COLUMNS (${filteredAvailableColumns.length})`}
                                        </GridItem>
                                        <GridItem xs={3}>
                                            <TableSettingsButton
                                                onClick={handleAddAllClick}
                                            >
                                                Add All
                                            </TableSettingsButton>
                                        </GridItem>
                                    </GridContainer>
                                </div>
                                <div
                                    className={clsx(
                                        sharedTableClasses.fullWidth,
                                        tableSettingsClasses.rowTitle,
                                        'column-content-title'
                                    )}
                                >
                                    <GridContainer
                                        alignItems="center"
                                        className={clsx(
                                            tableSettingsClasses.rowHeight,
                                            tableSettingsClasses.rowPadding,
                                            tableSettingsClasses.columnContentBorder
                                        )}
                                    >
                                        <GridItem
                                            xs={10}
                                            className={
                                                tableSettingsClasses.columnContentTitleMargin
                                            }
                                        >
                                            Name
                                        </GridItem>
                                        <GridItem>Add</GridItem>
                                    </GridContainer>
                                </div>
                                <div
                                    className={clsx(
                                        sharedTableClasses.fullWidth,
                                        tableSettingsClasses.columnContentList,
                                        'column-content-list'
                                    )}
                                >
                                    {filteredAvailableColumns.map(
                                        (column, idx) => {
                                            return (
                                                <GridContainer
                                                    alignItems="center"
                                                    key={idx}
                                                    className={clsx(
                                                        tableSettingsClasses.rowHeight,
                                                        tableSettingsClasses.rowPadding,
                                                        tableSettingsClasses.columnContentBorder,
                                                        'available-column-item-container'
                                                    )}
                                                >
                                                    <GridItem
                                                        xs={10}
                                                        className={clsx(
                                                            sharedTableClasses.textOverflowEllipsis,
                                                            'available-column-item-title'
                                                        )}
                                                    >
                                                        <CustomHoverTooltip
                                                            text={column.title}
                                                        />
                                                    </GridItem>
                                                    <GridItem xs={2}>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            aria-label="Add to selected columns"
                                                            classes={{
                                                                sizeSmall:
                                                                    tableSettingsClasses.addButtonSizeSmall,
                                                            }}
                                                            className="available-column-item-add-button"
                                                            data-column-id={
                                                                column.id
                                                            }
                                                            onClick={
                                                                handleAvailableColumnAddClick
                                                            }
                                                        >
                                                            <AddIcon fontSize="small" />
                                                        </IconButton>
                                                    </GridItem>
                                                </GridContainer>
                                            )
                                        }
                                    )}
                                </div>
                            </div>
                        </GridItem>
                        {/* Right side of Table settings */}
                        <GridItem
                            sm={12}
                            md={6}
                            className={sharedTableClasses.fullWidth}
                        >
                            {showGroupOptionSetting && (
                                <div
                                    className={clsx(
                                        tableSettingsClasses.groupOption,
                                        'group-option-toggle-container'
                                    )}
                                >
                                    <FormControlLabel
                                        className="group-option-toggle-label"
                                        label={`Group Option ${maxGroupCount > 0
                                            ? `(up to ${maxGroupCount} can be applied)`
                                            : ''
                                            }`}
                                        control={
                                            <Switch
                                                checked={groupEnabled}
                                                onChange={
                                                    handleGroupEnabledChange
                                                }
                                                color="primary"
                                                className="group-option-toggle"
                                                classes={{
                                                    colorPrimary:
                                                        tableSettingsClasses.groupEnabledSwitchPrimary,
                                                }}
                                            />
                                        }
                                    />
                                </div>
                            )}
                            <DndContext onDragEnd={handleDragDropContextEnd}>
                                {showGroupOptionSetting && groupEnabled && (
                                    <DroppableContainer
                                        droppableId={groupsDroppableId}
                                        title="GROUP COLUMNS"
                                        items={groupColumns}
                                        orderPrefix="G"
                                        pinningProps={pinningProps}
                                        pinOnFirstItem={true}
                                        pinOnLastItem={false}
                                        showContentHeader={true}
                                        showPinning={showPinning}
                                        showCollapseToggle={showCollapseToggle}
                                        collapseColumnCheckedState={
                                            collapseColumnChecked
                                        }
                                        handlePinClick={handlePinClick}
                                        handleRemoveAllClick={
                                            handleRemoveAllClick
                                        }
                                        handleRemoveClick={handleRemoveClick}
                                        handleCollapseCheck={
                                            handleCollapseColumnCheckChange
                                        }
                                    />
                                )}
                                <DroppableContainer
                                    handlePinClick={handlePinClick}
                                    handleRemoveAllClick={handleRemoveAllClick}
                                    handleRemoveClick={handleRemoveClick}
                                    droppableId={selectedColumnsDroppableId}
                                    title="SELECTED COLUMNS"
                                    items={selectedColumns}
                                    pinningProps={pinningProps}
                                    pinOnFirstItem={!groupColumns.length}
                                    pinOnLastItem={true}
                                    showContentHeader={
                                        !showGroupOptionSetting || !groupEnabled
                                    }
                                    showPinning={showPinning}
                                />
                            </DndContext>
                        </GridItem>
                    </GridContainer>
                </DialogContent>
                <DialogActions className={tableSettingsClasses.actionContainer}>
                    <GridItem
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <TableSettingsButton
                            disabled={isCurrentSettingsMatchingDefaults}
                            onClick={handleResetTableSettings}
                            {...{
                                style: {
                                    backgroundColor: isCurrentSettingsMatchingDefaults
                                        ? defaultSecondary
                                        : errorColor,
                                    cursor: isCurrentSettingsMatchingDefaults ? 'default' : 'pointer',
                                },
                            }}
                        >
                            Reset Defaults
                        </TableSettingsButton>
                    </GridItem>
                    <GridItem
                        className={tableSettingsClasses.actionButtonWrapper}
                    >
                        <ActionButton
                            color="secondary"
                            onClick={handleDialogClose}
                            loading={isListingLoading}
                        >
                            Cancel
                        </ActionButton>
                        <ActionButton
                            color="primary"
                            onClick={handleSaveClick}
                            loading={isListingLoading}
                            {...{ style: { marginLeft: 10 } }}
                        >
                            Save
                        </ActionButton>
                    </GridItem>
                </DialogActions>
            </Dialog>
            <LoadingBackdrop isVisible={isListingLoading} />
            <ConfirmationDialog
                openDialog={showDialogWarning}
                dialogHeading="Confirm"
                contentNode='At least one "Selected Column" is required on the table'
                confirmMessage=""
                dialogActionCallback={handleAcknowledgeDialogWarning}
                size="small"
                cancelText=""
                confirmText="OK"
            />
        </>
    )
}
